import { useCallback, useEffect, useMemo, useState } from 'react';

import { FilterChangeHandlers } from './list-filter-types';
import { forceLocalDate } from '../dates';

/**
 * Builds a set of active filter keys based on the current filter state and range filter keys.
 * @param filters - The current filter state
 * @param rangeFilterKeys - An array of keys corresponding to range/dateRange filters
 * @returns A set of active filter keys
 */
const buildInitialActiveKeys = <TFilters>(filters: TFilters, rangeFilterKeys: readonly string[]): Set<string> => {
  const keys = new Set<string>();
  for (const key of rangeFilterKeys) {
    const filter = (filters as Record<string, unknown>)[key] as { from?: unknown; to?: unknown } | undefined;
    if (filter && (filter.from !== undefined || filter.to !== undefined)) {
      keys.add(key);
    }
  }
  return keys;
};

interface UseFiltersSidebarArgs<TFilters> {
  /** Default (empty) filter state used for initialization and clearAll */
  defaultFilters: TFilters;
  /** Keys of range/dateRange filters that use the "Show all" / "Filter by range" toggle */
  rangeFilterKeys: readonly string[];
}

export interface UseFiltersSidebarReturn<TFilters> {
  filters: TFilters;
  setFilters: (filters: TFilters) => void;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  apply: () => void;
  cancel: () => void;
  clearAll: () => void;
  draftFilters: TFilters;
  setDraftFilters: (filters: TFilters) => void;
  activeFilterKeys: Set<string>;
  handlers: FilterChangeHandlers;
}

/**
 * Custom hook for managing the state and behavior of a filters sidebar.
 * @param defaultFilters - The default filter state used for initialization and when clearing filters
 * @param rangeFilterKeys - An array of keys corresponding to filters that have a range (e.g. "totalDue") and use the "Show all" / "Filter by range" toggle
 * @returns An object containing the current filter state, draft filter state, active filter keys, and handlers for managing the filters sidebar
 */
export const useFiltersSidebar = <TFilters>({
  defaultFilters,
  rangeFilterKeys
}: UseFiltersSidebarArgs<TFilters>): UseFiltersSidebarReturn<TFilters> => {
  const [filters, setFilters] = useState<TFilters>(defaultFilters);
  const [isOpen, setIsOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<TFilters>(defaultFilters);
  const [activeFilterKeys, setActiveFilterKeys] = useState<Set<string>>(() =>
    buildInitialActiveKeys(draftFilters, rangeFilterKeys)
  );

  const open = useCallback(() => {
    setDraftFilters(filters);
    setIsOpen(true);
  }, [filters]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const apply = useCallback(() => {
    setFilters(draftFilters);
    setIsOpen(false);
  }, [draftFilters]);

  const cancel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const clearAll = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  // Snapshot active keys when sidebar opens — intentionally excludes draftFilters
  // to avoid re-running when user edits drafts inside the open sidebar
  useEffect(() => {
    if (isOpen) {
      setActiveFilterKeys(buildInitialActiveKeys(draftFilters, rangeFilterKeys));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, rangeFilterKeys]);

  // -- Draft filter change handlers ------------------------------------------

  const handleTextMatchChange = useCallback((key: string, selectedValues: string[]) => {
    setDraftFilters(prev => ({ ...prev, [key]: { selectedValues } }));
  }, []);

  // Toggles a range/dateRange filter between "Show all" and "Filter by range".
  // When deactivated, also clears the draft values so the filter resets.
  const handleToggle = useCallback((key: string, isActive: boolean) => {
    if (isActive) {
      setActiveFilterKeys(prev => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    } else {
      setActiveFilterKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setDraftFilters(prev => ({ ...prev, [key]: {} }));
    }
  }, []);

  const handleRangeFromChange = useCallback((key: string, value: string) => {
    setDraftFilters(prev => {
      const current = (prev as Record<string, unknown>)[key] as { from?: number; to?: number } | undefined;
      return { ...prev, [key]: { ...current, from: value ? parseFloat(value) : undefined } };
    });
  }, []);

  const handleRangeToChange = useCallback((key: string, value: string) => {
    setDraftFilters(prev => {
      const current = (prev as Record<string, unknown>)[key] as { from?: number; to?: number } | undefined;
      return { ...prev, [key]: { ...current, to: value ? parseFloat(value) : undefined } };
    });
  }, []);

  const handleDateRangeFromChange = useCallback((key: string, rawValue: string) => {
    setDraftFilters(prev => {
      const current = (prev as Record<string, unknown>)[key] as { from?: Date; to?: Date } | undefined;
      return { ...prev, [key]: { ...current, from: rawValue ? forceLocalDate(rawValue) : undefined } };
    });
  }, []);

  const handleDateRangeToChange = useCallback((key: string, rawValue: string) => {
    setDraftFilters(prev => {
      const current = (prev as Record<string, unknown>)[key] as { from?: Date; to?: Date } | undefined;
      return { ...prev, [key]: { ...current, to: rawValue ? forceLocalDate(rawValue) : undefined } };
    });
  }, []);

  const handleSelectChange = useCallback((key: string, optionValue: string, checked: boolean) => {
    setDraftFilters(prev => {
      const current = (prev as Record<string, unknown>)[key] as
        | { selectedValues?: Record<string, boolean> }
        | undefined;
      return { ...prev, [key]: { selectedValues: { ...current?.selectedValues, [optionValue]: checked } } };
    });
  }, []);

  const handlers: FilterChangeHandlers = useMemo(
    () => ({
      handleTextMatchChange,
      handleToggle,
      handleRangeFromChange,
      handleRangeToChange,
      handleDateRangeFromChange,
      handleDateRangeToChange,
      handleSelectChange
    }),
    [
      handleTextMatchChange,
      handleToggle,
      handleRangeFromChange,
      handleRangeToChange,
      handleDateRangeFromChange,
      handleDateRangeToChange,
      handleSelectChange
    ]
  );

  return {
    filters,
    setFilters,
    isOpen,
    open,
    close,
    apply,
    cancel,
    clearAll,
    draftFilters,
    setDraftFilters,
    activeFilterKeys,
    handlers
  };
};
